'use client';

import { logout } from "@/actions/auth";
import { Language } from "@/components/forms/language";
import { TablatureSearchForm } from "@/components/layout/omnisearch";
import { useTranslation } from "@/lib";
import { UserProfile } from "@/types/users";
import classNames from "classnames";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button, LocalizedLink } from '@ui/primitives';

interface MenuProps {
    locale: string;
    dictionary: Record<string, string>;
    profile?: UserProfile | null;
    logoPath: string;
}

const LogoutButton = ({ children }: { children: React.ReactNode }) => (
    <form action={logout}>
        <Button type="submit" variant="reverse">
            {children}
        </Button>
    </form>
);

export const Menu = ({ dictionary, locale, profile, logoPath }: MenuProps) => {
    const [menuState, setMenuState] = useState<boolean>(false);
    const { t } = useTranslation();

    const navigationItems = [
        { href: "/", icon: "fa fa-home", label: t('menu.home') },
        { href: "/tablatures", icon: "fa fa-music", label: t('menu.tablatures') },
        { href: "/news", icon: "fa fa-newspaper", label: t('menu.news') },
        { href: "/forum", icon: "fa fa-comments", label: t('menu.forum') },
    ];

    const toggleMenu = (state?: boolean) => {
        setMenuState(state ?? !menuState);
    };

    const renderLogo = () => (
        <LocalizedLink locale={locale} href="/" className="flex items-center display-block h-[40px] overflow-hidden">
            <img src={logoPath} alt="Tablatures Harmonica Logo" className="w-[300px] h-[40px] overflow-hidden" title="Tablatures-Harmonica" />
        </LocalizedLink>
    );

    const renderNotLogged = () => (
        <>
            <LocalizedLink locale={locale} href={"/login"} variant="reverse">
                <i className="fa-solid fa-sign-in mr-2" />
                {t('menu.login')}
            </LocalizedLink>
            <LocalizedLink locale={locale} href={"/register"} variant="reverse">
                <i className="fa-solid fa-user-plus mr-2" />
                {t('menu.register')}
            </LocalizedLink>
        </>
    )

    const renderLogged = (profile: UserProfile) => (
        <>
            <LocalizedLink locale={locale} href={"/profile"} variant="reverse">
                <i className="fa-solid fa-user mr-2" />
                {t('users.menu_profile')}
            </LocalizedLink>
            <LogoutButton>
                {t('menu.logout')}
            </LogoutButton>
        </>
    )

    const submenu = (
        <nav className="bg-white border-b border-gray-200 shadow-sm">
            <div className="max-w-7xl mx-auto px-4">
                <div className="hidden lg:flex items-center justify-center space-x-1 overflow-x-auto">
                    {navigationItems.map((item) => (
                        <LocalizedLink
                            locale={locale}
                            key={item.href}
                            href={item.href}
                            variant="ghost"
                            className="flex items-center gap-2 px-9 py-1 my-2 rounded-lg font-medium whitespace-nowrap"
                        >
                            <i className={item.icon} />
                            <span className="text-sm">{item.label}</span>
                        </LocalizedLink>
                    ))}
                </div>
            </div>
        </nav>
    );

    const sidemenu = (
        <div className={classNames("fixed inset-0 z-50 lg:hidden", menuState ? "opacity-100" : "opacity-0 pointer-events-none")}>
            <div className="absolute inset-0 bg-black/50" onClick={() => toggleMenu(false)} />
            <div className={classNames("absolute right-0 top-0 h-full w-80 max-w-[80vw] bg-white shadow-xl transition-transform duration-300", menuState ? "translate-x-0" : "translate-x-full")}>
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-4 border-b bg-primary-600 text-white">
                        <span className="font-semibold">{t('menu.title')}</span>
                        <Language profileId={profile?.id ? String(profile.id) : undefined} />
                        <Button onClick={() => toggleMenu(false)} variant="reverse" icon={<i className="fa-solid fa-xmark" />} />
                    </div>
                    <nav className="flex-1 overflow-y-auto py-4">
                        <div className="space-y-1 px-3">
                            {navigationItems.map((item) => (
                                <LocalizedLink
                                    locale={locale}
                                    key={item.href}
                                    href={item.href}
                                    variant="ghost"
                                    className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium border-b border-gray-200 rounded-none"
                                    onClick={() => toggleMenu(false)}
                                >
                                    <i className={item.icon} />
                                    <span>{item.label}</span>
                                </LocalizedLink>
                            ))}
                        </div>
                    </nav>
                    <div className="p-4 border-t mt-auto">
                        {!profile ? (
                            <div className="space-y-2 flex flex-col items-center">
                                <LocalizedLink locale={locale} href="/login" variant="secondary" className="w-full" onClick={() => toggleMenu(false)}>
                                    <i className="fa-solid fa-sign-in mr-2" />
                                    {t('menu.login')}
                                </LocalizedLink>
                                <LocalizedLink locale={locale} href="/register" variant="secondary" className="w-full" onClick={() => toggleMenu(false)}>
                                    <i className="fa-solid fa-user-plus mr-2" />
                                    {t('menu.register')}
                                </LocalizedLink>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex items-center gap-3 px-4 py-2">
                                    <div className="w-10 h-10 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-medium">
                                        {profile.displayName?.[0]?.toUpperCase()}
                                    </div>
                                    <span className="font-medium text-gray-900">{profile.displayName}</span>
                                </div>
                                <LocalizedLink locale={locale} href="/profile" variant="ghost" className="flex items-center gap-3 px-4 py-2 rounded-lg" onClick={() => toggleMenu(false)}>
                                    <i className="fa-solid fa-user" />
                                    {t('users.menu_profile')}
                                </LocalizedLink>
                                <form action={logout} className="w-full">
                                    <button type="submit" className="flex items-center gap-3 w-full px-4 py-2 text-red-700 hover:bg-red-50 rounded-lg text-left">
                                        <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
                                        {t('menu.logout')}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const menuWindow = (
        <header className="bg-primary-500 hidden lg:block shadow-lg sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    <div className="flex-shrink-0 h-10 w-[300px]">{renderLogo()}</div>
                    <div className="flex-1 max-w-md mx-8"><TablatureSearchForm dictionary={dictionary} /></div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            {!profile ? renderNotLogged() : renderLogged(profile)}
                        </div>
                        <Language profileId={profile?.id ? String(profile.id) : undefined} />
                    </div>
                </div>
            </div>
            {submenu}
        </header>
    );

    const menuMobile = (
        <header className="bg-primary-700 block lg:hidden shadow-lg sticky top-0 z-40">
            <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex">{renderLogo()}</div>
                    <div className="flex items-center gap-2">
                        <div className="hidden sm:block"><TablatureSearchForm dictionary={dictionary} /></div>
                        <div>
                            <button className=" text-white hover:bg-white/10 p-2 rounded-lg" onClick={() => toggleMenu()}>
                                <i className="fas fa-bars fa-lg" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="sm:hidden mt-3"><TablatureSearchForm dictionary={dictionary} /></div>
            </div>
        </header>
    );

    return (
        <>
            {menuWindow}
            {menuMobile}
            {sidemenu}
        </>
    );
};


